import { SingleResponse, ApplicationTableDatum, BankAccountType, CustomerApplicationProduct, CustomerType, IdentificationType, MaritalStatus, Sex, Title, VendorApplication } from "common";
import { BankAccountTypesOptions, MaritalStatuses, TitleOptions } from "./constants";
import { FormGroup, FormControl } from "@angular/forms";

export function createBlobUrl(blob: Blob) {
    // const blob = this.dataURItoBlob(base64Image);
    const imageURL = URL.createObjectURL(blob);
    return imageURL;
}

export function base64toBlob(dataURI: string) {
    const byteString: string = atob(dataURI.split(',')[1]);

    const mimeType: string = dataURI.split(',')[0].split(':')[1].split(';')[0];

    const byteArray: ArrayBuffer = new ArrayBuffer(byteString.length);

    const intArray: Uint8Array = new Uint8Array(byteArray);

    for (let i = 0; i < byteString.length; i++) {
        intArray[i] = byteString.charCodeAt(i);
    }

    return new Blob([byteArray], { type: mimeType });
}

export function blobToBase64(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader: FileReader = new FileReader();

        reader.onloadend = () => resolve(reader.result as string);

        reader.readAsDataURL(blob);

        reader.onerror = reject;
    });
}

export function unit8ArrayToBase64(array: Uint8Array): string {
    const binary: string = array.reduce((data, byte) => data + String.fromCharCode(byte), '');

    return btoa(binary);
}

export function base64ToUnit8Array(base64: string): Uint8Array {
    const binary: string = atob(base64);

    const bytes: Uint8Array = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    return bytes;
}

/**
 * Converts the pay cycle value to a human-readable string.
 *
 * @param {(Title | null)} [value] The value to convert.
 * @return {string} The human-readable string.
 */
export function toTitle(value?: Title | null): string {
    return TitleOptions.find(title => title.value === value)?.display || '';
}

export function toBankType(value: BankAccountType): string {
    return BankAccountTypesOptions.find(type => type.value === value)?.display || 'Unknown';
}

export function toMaterialStatus(value: MaritalStatus): string {
    return MaritalStatuses.find(status => status.value === value)?.display || 'Unknown';
}

export function toNth(n: number): string {
    const s: string[] = ['th', 'st', 'nd', 'rd'],
        v: number = n % 100;

    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Returns a key-value pair of errors for the given control.
 * key = form control name.
 * value = error object.
 */
export function errorsOf(control: FormGroup | FormControl | null): null | Record<string, string> {
    if (!control) {
        return null;
    }

    if (control instanceof FormControl) {
        return control.errors;
    }
    else if (control instanceof FormGroup) {
        return Object.keys(control.controls).reduce((acc, key) => {
            const errors = errorsOf(control.controls[key] as any);

            if (errors) {
                acc[key] = errors;
            }

            return acc;
        }, {} as any);
    }

    return null;
}

/**
 * This parsing function if common between all tables within the OZ application.
 * Merges the data from either params into a single array of ApplicationTableDatum.
 * 
 * This is safe to call with either or both of the parameters being empty/undefined.
 *
 * @param {SingleResponse<CustomerApplicationProduct[]>} [productApplications={ total: 0, result: [] }] The product applications.
 * @param {SingleResponse<VendorApplication[]>} [stagingApplications={ total: 0, result: [] }] The staging applications.
 */
export function parseResponseToTableData(productApplications: SingleResponse<CustomerApplicationProduct[]> = { total: 0, result: [] }, stagingApplications: SingleResponse<VendorApplication[]> = { total: 0, result: [] }) {

    // Parse product applications first.
    return productApplications.result.map<ApplicationTableDatum>(application => ({
        reference: application.reference,
        patientId: application.customer.customerInfo.customerIdentity,
        patientSurname: application.customer.customerInfo.lastName,
        patientName: application.customer.customerInfo.name,
        applicationAmount: application.baseAmount,
        applicationDatum: application
    }))
        // Then parse staging applications.
        .concat(stagingApplications.result.map<ApplicationTableDatum>(application => {

            let debtor = application.patients?.find(p => p.id === application.debtorCustomerId);
            if (!debtor) {
                debtor = application.patients?.find(p => p.customerType === CustomerType.Debtor || p.customerType === CustomerType.DebtorAndAuthorizer || p.customerType === CustomerType.PatientDebtorAndAuthorizer);
            }

            return {
                reference: application.reference,
                patientId: debtor?.identityNumber || 'N/A',
                patientSurname: debtor?.lastName || 'N/A',
                patientName: debtor?.firstName || 'N/A',
                applicationAmount: application.loanAmount,
                applicationDatum: application,
                submitted: false
            };
        }));
}

/**
 * Format currency to use commas separator and end with two decimal places, e.g. 1000000 -> 1,000,000.00
 *
 * @param {string | number} value The value to format.
 * @return {string} The formatted currency.
 */
export function formatCurrency(value: string | number): string {
    // Ensure the value is a number
    const num: number = +value;

    // Split the number into integer and decimal parts
    const [integerPart, decimalPart]: string[] = num.toFixed(2).split('.');

    // Format the integer part with commas
    const formattedIntegerPart: string = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Combine the formatted integer part with the decimal part
    return 'R' + formattedIntegerPart + '.' + decimalPart;
}

/**
 * Format the given bytes into a human-readable string.
 *
 * @param {number} bytes The bytes to format.
 * @param {number} [decimals=2] The number of decimal places to use.
 * @return {string} The formatted bytes.
 * @source https://stackoverflow.com/a/18650828/116462
 */
export function formatBytes(bytes: number, decimals = 2): string {
    if (!+bytes) {
        return '0 Bytes'
    }

    const kilobyte: number = 1024
    const decimalPlaces: number = decimals < 0 ? 0 : decimals
    const sizes: string[] = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']

    const index: number = Math.floor(Math.log(bytes) / Math.log(kilobyte))

    return `${parseFloat((bytes / Math.pow(kilobyte, index)).toFixed(decimalPlaces))} ${sizes[index]}`
}

/**
 * Creates a responsive dialog size object based on the current window width.
 *
 * @return {{ width: string; height: string; maxWidth?: string; maxHeight?: string }} An object containing the width, height, and optional maxWidth and maxHeight properties.
 */
export function createResponsiveDialogSize(): { width: string; height: string; maxWidth?: string; maxHeight?: string } {
    // Default width and height for the dialog.
    const dialogSize = {
        width: '90%',
        height: '90%',
        maxWidth: window.innerWidth < 724 ? 'unset' : undefined,
        maxHeight: window.innerWidth < 724 ? 'unset' : undefined
    }

    if (window.innerWidth < 524) {
        dialogSize.width = '100%';
        dialogSize.height = '100%';
    }
    else if (window.innerWidth < 724) {
        dialogSize.width = '95%';
        dialogSize.height = '95%';
    }

    return dialogSize;
}

// TODO it would be cool if this returned some kind of "is" operation to prevent us from using as casting.
export function isNullOrUndefiend<T>(...args: T[]): boolean {
    return args.some((argument: T) => argument === undefined && argument === null)
}

/**
 * Derives Sex from an RSA ID number. Digits 7-10 < 5000 => Female, else Male.
 * Returns null if not an RSA ID or the number is too short / unparseable.
 */
export function deriveSexFromRSAId(idType: IdentificationType | null | undefined, idNumber: string | null | undefined): Sex | null {
    if (idType !== IdentificationType.RSAID || !idNumber || idNumber.length < 10) {
        return null;
    }
    const digits: number = parseInt(idNumber.substring(6, 10), 10);
    if (isNaN(digits)) {
        return null;
    }
    return digits < 5000 ? Sex.Female : Sex.Male;
}

export function deepCopy<T>(source: T): T {
    return JSON.parse(JSON.stringify(source));
}

export function isEqual<T>(obj1: T, obj2: T): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
}

export function deleteNullOrEmptyProperties(obj: Record<string, unknown>): Record<string, unknown> {
    Object.keys(obj).forEach(key => {
        const value = obj[key];
        if (value === null || value === undefined || value === '') {
            delete obj[key];
        } else if (typeof value === 'object' && !Array.isArray(value)) {
            deleteNullOrEmptyProperties(value as Record<string, unknown>);
        }
    });
    return obj;
}